import type {
  Block,
  BlockRelationship,
  RootBlock,
} from '@basehub/data/replicache/index'
import {
  getChildrenKeys,
  isBlockRelationship,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { getTreeManager } from '@basehub/utils/tree/index'
import { invariant } from '@epic-web/invariant'
import { decompress, Compressed } from 'compress-json'
import { Replicache } from 'replicache'
import { useReplicacheParamsStore } from '~/replicache/frontend/init'
import { repoSpaceMutators } from '~/replicache/schema/repo-space/mutators'
import { useBlocksMapStore } from './blocks-map-store'

export const DEBUG = false

export type GetCommitBlockTreeManagerQueryFnProps = {
  commitId: string
  rootBlockId: string
  spaceId: string | null | undefined
  replicache: Replicache<typeof repoSpaceMutators> | null | undefined
  prefetched?: { blocks: Block[]; brs: BlockRelationship[] }
  forcePull?: boolean
}

export const getCommitBlockTreeManagerQueryFn = async ({
  commitId,
  rootBlockId,
  spaceId,
  prefetched,
  replicache,
  forcePull,
}: GetCommitBlockTreeManagerQueryFnProps) => {
  if (DEBUG) {
    console.log('————getCommitBlockTreeManagerQueryFn————')
    console.log({ commitId, rootBlockId, spaceId, prefetched, forcePull })
    // const freshFromReplicache = await replicache?.query((tx) =>
    //   tx.get(`block/${rootBlockId}`)
    // )
    // console.log({ freshFromReplicache })
  }

  invariant(spaceId, 'spaceId is undefined')
  const bStore = useBlocksMapStore.getState()
  const blocksMap = bStore.blocksMap
  const brsMap = bStore.brsMap

  let rootBlock: RootBlock | undefined

  const getBlockOrThrow = (id: string) => {
    const block = blocksMap.get(id)
    invariant(block, `block ${id} not found`)
    return block
  }

  const getBROrThrow = (id: string) => {
    invariant(rootBlock, 'rootBlock not found')
    const blockRelationshipsMap = bStore.getBrsByRootId(rootBlock)
    const br = blockRelationshipsMap.get(id)
    if (!br) {
      console.log(rootBlockId)
      console.log([...blockRelationshipsMap.values()])
      console.log(blockRelationshipsMap?.size())
    }
    invariant(br, `br ${id} not found`)
    return br
  }

  if (prefetched) {
    if (DEBUG) {
      console.log(
        `Using prefetched blocks (${prefetched.blocks.length}) & brs ${prefetched.brs.length}`
      )
    }
    prefetched.blocks.forEach((b) => {
      blocksMap.set(b.id, b)
      if (isRootBlock(b)) {
        rootBlock = b
      }
    })
    prefetched.brs.forEach((br) => {
      brsMap.set(br.id, br)
    })
  } else if (replicache) {
    if (DEBUG) console.log('Using replicache to fetch blocks')
    invariant(rootBlockId, 'rootBlockId is undefined')

    // we'll check in the replicache store to see if we have the blocks there
    const foundRootBlock = blocksMap.get(rootBlockId) as RootBlock | undefined

    const shouldPull = !foundRootBlock || forcePull

    if (DEBUG) {
      console.log('Replicache state:', {
        foundRootBlock: Boolean(foundRootBlock),
        shouldPull,
        blocksInMap: blocksMap.size,
      })
    }

    if (shouldPull) {
      if (DEBUG) console.log('Pulling new blocks from replicache')
      // we'll need to re-pull replicache
      useReplicacheParamsStore.setState((p) => {
        return {
          repo: {
            ...p.repo,
            requestCommitRootBlockIds: Array.from(
              new Set([
                ...(p.repo.requestCommitRootBlockIds ?? []),
                rootBlockId,
              ])
            ),
          },
        }
      })

      /** Pull and make sure the blocks arrive to the blocksMap before continuing */
      await new Promise<void>((resolve, reject) => {
        let pullDone = false
        let receivedPull = false
        let pullTimeout = 0
        let pullInterval = 0

        const done = (result: void | Error) => {
          receivedPull = true
          unsub()
          window.clearTimeout(pullTimeout)
          window.clearInterval(pullInterval)
          if (result instanceof Error) {
            reject(result)
          } else {
            resolve()
          }
        }

        const unsub = replicache.experimentalWatch(
          (diff) => {
            if (DEBUG) {
              console.log('Replicache diff received:', diff.length, 'changes')
            }
            let foundRootBlock = false
            diff.forEach((diffOp) => {
              if (
                (diffOp.key.startsWith('block/') ||
                  diffOp.key.startsWith('br/')) === false
              ) {
                return
              }
              if (diffOp.op === 'add' || diffOp.op === 'change') {
                if (diffOp.key.startsWith('block/')) {
                  const block = diffOp.newValue as Block
                  if (block.deletedAt) return
                  blocksMap.set(block.id, block)
                  if (isRootBlock(block) && block.id === rootBlockId) {
                    rootBlock = block
                    foundRootBlock = true
                    if (DEBUG) console.log('Root block found:', block.id)
                  }
                } else {
                  const br = diffOp.newValue as BlockRelationship
                  if (br.deletedAt) return
                  brsMap.set(br.id, br)
                }
              }
            })
            window.setTimeout(() => {
              if (pullDone && foundRootBlock) {
                done()
              }
            }, 0)
          },
          { initialValuesInFirstDiff: false }
        )

        pullInterval = window.setInterval(() => {
          try {
            rootBlock = getBlockOrThrow(rootBlockId) as RootBlock
            if (DEBUG) console.log('Pull complete, root block found')
            done()
            return
          } catch (err) {
            if (DEBUG) console.log('Pull complete but root block not found')
            // ignore
          }
        }, 100)

        replicache.pull().then(() => {
          pullDone = true
          pullTimeout = window.setTimeout(() => {
            if (!receivedPull) {
              done(new Error('timeout getting blocks map'))
            }
          }, 60_000)
        })
      })

      // cleanup so we don't pull the same tree again
      useReplicacheParamsStore.setState((p) => {
        return {
          repo: {
            ...p.repo,
            requestCommitRootBlockIds: [
              ...(p.repo.requestCommitRootBlockIds ?? []).filter(
                (id) => id !== rootBlockId
              ),
            ],
          },
        }
      })
    }
    if (DEBUG) {
      console.log({ shouldPull, foundRootBlock, forcePull })
    }
    rootBlock = getBlockOrThrow(rootBlockId) as RootBlock
  } else {
    if (DEBUG) console.log('Fetching blocks from API')
    const res = await fetch('/api/get-blocks-under-commit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ spaceId, commitId }),
    })
    const { blocksAndBrsCompressed } = (await res.json()) as {
      blocksAndBrsCompressed: Compressed
    }
    const blocksAndBrs = decompress(blocksAndBrsCompressed) as (
      | Block
      | BlockRelationship
    )[]

    if (DEBUG) {
      console.log('Received blocks and brs from API:', blocksAndBrs.length)
    }
    blocksAndBrs.forEach((b) => {
      if (isBlockRelationship(b)) {
        brsMap.set(b.id, b)
      } else {
        blocksMap.set(b.id, b)
        if (isRootBlock(b)) {
          rootBlock = b
          if (DEBUG) console.log('Root block found:', b.id)
        }
      }
    })
  }

  invariant(rootBlock, 'rootBlock not found')

  if (DEBUG) {
    console.log('Final state:', {
      rootBlock,
      blocksInMap: blocksMap.size(),
      doud: [...bStore.getBrsByRootId(rootBlock).values()],
    })
  }

  const tm = getTreeManager<Block, NonNullable<unknown>>({
    rootKey: rootBlock.id,
    getKey: (block) => block.id,
    getOrder: (key) => {
      const br = getBROrThrow(key)
      return br.order
    },
    getValue: getBlockOrThrow,
    getChildrenKeys: (_key, block) => {
      invariant(rootBlock, 'rootBlock not found')
      const blockRelationshipsMap = bStore.getBrsByRootId(rootBlock)
      return getChildrenKeys({
        block,
        blockRelationshipsMap,
        blocksMap,
        rootBlockId: rootBlockId,
      })
    },
    indexKeys: ['sourceBlockId'],
    autoFallbackWithKeys: ['sourceBlockId'],
  })

  return tm
}
